/**
 * FreeGiveawayHero — 100% free concierge-audit lead magnet at `/free`.
 *
 * Same left-column pitch as `HomeHero4Step`. The right card is a 2-step
 * lead form:
 *   Step 1 — Paste LinkedIn URL + pick a goal
 *              CTA: "Tell us where to send your new profile"
 *   Step 2 — Name, email, phone
 *              CTA: "Send me my new profile"
 *
 * On submit we POST `/api/free-giveaway/request` which stores the lead
 * in the `free_giveaway_leads` collection, emails the visitor an
 * acknowledgement + pings the internal inbox for manual fulfilment.
 * Nothing here touches the automated audit pipeline (which is PDF-only).
 */
import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Loader2, CheckCircle2,
  Briefcase, User as UserIcon, TrendingUp, Star,
  Eye, ShieldCheck, MessageCircle, Gift, Rocket, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { getReferralContext } from '../../lib/referralTracker';
import { trackLead } from '../../lib/analytics';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const GOALS = [
  {
    key: 'recruiter_visibility',
    icon: Briefcase,
    label: 'Get noticed by recruiters',
    body: 'Improve search visibility and communicate your professional value.',
  },
  {
    key: 'thought_leadership',
    icon: Rocket,
    label: 'Build thought leadership',
    body: 'Establish authority and make your expertise more memorable.',
  },
  {
    key: 'business_development',
    icon: TrendingUp,
    label: 'Win clients and business opportunities',
    body: 'Position yourself clearly for prospects, partners and decision-makers.',
  },
];

const LINKEDIN_URL_RE = /linkedin\.com\/in\//i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function FreeGiveawayHero() {
  const [step, setStep] = useState(0);
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [goal, setGoal] = useState('recruiter_visibility');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleContinue = () => {
    const trimmed = (linkedinUrl || '').trim();
    if (!trimmed || !LINKEDIN_URL_RE.test(trimmed)) {
      setErrors({ linkedin_url: 'Enter a valid LinkedIn profile URL — e.g. https://linkedin.com/in/your-handle.' });
      toast.error('That does not look like a LinkedIn profile URL.');
      return;
    }
    setErrors({});
    setStep(1);
  };

  const validateContact = () => {
    const errs = {};
    if (!(name || '').trim() || name.trim().length < 2) errs.name = 'Enter your full name.';
    if (!EMAIL_RE.test((email || '').trim())) errs.email = 'Enter a valid email address.';
    if ((phone || '').replace(/\D/g, '').length < 8) errs.phone = 'Enter a valid phone number.';
    if (!consent) errs.consent = 'Please confirm the consent before we can send your new profile.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = useCallback(async () => {
    if (!validateContact()) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/free-giveaway/request`, {
        linkedin_url: linkedinUrl.trim(),
        goal,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        consent: true,
        referral: getReferralContext(),
      }, { timeout: 20000 });
      setSubmitted(true);
      trackLead('free_giveaway_hero', { goal });
      toast.success('You’re in! We’ll email your new profile shortly.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object'
        ? (detail?.message || 'Could not send your request. Please try again.')
        : (detail || 'Could not send your request. Please try again.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [linkedinUrl, goal, name, email, phone, consent]);

  return (
    <section
      className="relative overflow-x-clip isolate bg-[#050b1a] text-slate-100"
      aria-label="Free LinkedIn profile makeover"
      id="free-giveaway-hero"
      data-testid="free-giveaway-hero"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(1100px circle at 78% 40%, hsl(190 95% 55% / 0.12), transparent 60%), radial-gradient(700px circle at 8% 8%, hsl(212 92% 55% / 0.09), transparent 60%)',
        }}
      />
      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-10 py-10 sm:py-12">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-start">
          {/* LEFT — persistent pitch (mirrors HomeHero4Step) */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:sticky lg:top-24 lg:self-start"
          >
            <p className="font-mono-edge text-[11px] tracking-[0.24em] uppercase text-yellow-300">
              LinkedIn Profile Optimization
            </p>
            <h1 className="mt-3 font-display font-semibold tracking-[-0.02em] leading-[1.05] text-white text-[32px] sm:text-[38px] lg:text-[42px]">
              Turn your LinkedIn profile into more{' '}
              <span className="text-emerald-400">interviews</span>,{' '}
              <span className="text-rose-400">clients</span> and{' '}
              <span className="text-sky-400">influence</span>.
            </h1>
            <p className="mt-3.5 text-[14.5px] text-slate-300/90 leading-relaxed max-w-[520px]">
              Our team audits exactly what&apos;s helping — or hurting — your profile, then emails you a fully rewritten version, ready to publish. Zero cost, zero guesswork.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2.5 max-w-[440px]">
              {[
                { icon: Briefcase, label: 'Corporate Professionals', color: 'sky' },
                { icon: UserIcon, label: 'Job Seekers', color: 'emerald' },
                { icon: TrendingUp, label: 'Consultants', color: 'blue' },
                { icon: Star, label: 'Coaches', color: 'rose' },
              ].map(({ icon: Icon, label, color }) => (
                <div
                  key={label}
                  className={`rounded-2xl border border-${color}-400/30 bg-${color}-500/[0.06] px-3 py-2.5 flex items-center gap-2`}
                >
                  <Icon size={16} className={`text-${color}-300`} strokeWidth={2.1} />
                  <p className="text-[12.5px] font-semibold text-slate-100 leading-tight">{label}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5 max-w-[520px]">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/30 bg-sky-500/[0.08] px-2.5 py-1 text-[11.5px] text-slate-100">
                <Eye size={12} className="text-sky-300" /> Better visibility
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-400/30 bg-blue-500/[0.08] px-2.5 py-1 text-[11.5px] text-slate-100">
                <ShieldCheck size={12} className="text-blue-300" /> Credible positioning
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-yellow-400/40 bg-yellow-500/[0.10] px-2.5 py-1 text-[11.5px] text-yellow-100">
                <MessageCircle size={12} className="text-yellow-300" /> More qualified conversations
              </span>
            </div>
          </motion.div>

          {/* RIGHT — free giveaway form card */}
          <motion.div
            key={`fg-step-${submitted ? 'done' : step}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-[22px] border border-white/10 bg-white p-5 sm:p-6 text-slate-900 shadow-2xl shadow-sky-900/40 min-h-[420px]"
            data-testid={`free-card-step-${submitted ? 'done' : step + 1}`}
          >
            <div className="relative inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 text-white px-4 py-1.5 text-[13px] font-extrabold tracking-[0.06em] shadow-[0_10px_30px_-8px_rgba(16,185,129,0.55)] ring-2 ring-emerald-300/50">
              <span
                aria-hidden="true"
                className="absolute -inset-0.5 -z-10 rounded-full bg-emerald-400/40 blur-md animate-pulse"
              />
              <Gift size={14} strokeWidth={2.4} />
              100% FREE
              <span className="hidden sm:inline text-emerald-100/90 font-semibold tracking-normal text-[11.5px]">· No credit card</span>
            </div>

            {submitted ? (
              <div data-testid="free-card-done">
                <h2 className="mt-3 text-[22px] font-bold text-slate-900 leading-tight">
                  Your new profile is being crafted.
                </h2>
                <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">
                  We&apos;ll email your complete audit and rewritten LinkedIn profile
                  to <b>{email}</b> within <b>24 business hours</b>. Keep an eye on your inbox
                  (and maybe your spam folder, just in case).
                </p>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-mono-edge tracking-[0.16em] uppercase text-slate-500">Your request</p>
                  <div className="mt-2 space-y-1 text-[13px] text-slate-800">
                    <div><span className="text-slate-500">LinkedIn:</span> {linkedinUrl}</div>
                    <div>
                      <span className="text-slate-500">Goal:</span>{' '}
                      {GOALS.find((g) => g.key === goal)?.label || goal}
                    </div>
                    <div><span className="text-slate-500">Deliver to:</span> {email}</div>
                  </div>
                </div>
                <div className="mt-5 rounded-2xl bg-sky-50/70 border border-sky-200 p-3.5 flex items-start gap-3">
                  <Sparkles size={16} className="text-sky-600 mt-0.5 shrink-0" />
                  <p className="text-[12.5px] text-slate-700 leading-snug">
                    Want to see what a completed Blayzrr audit looks like?{' '}
                    <a href="/methodology" className="text-sky-700 underline font-semibold">Read our methodology →</a>
                  </p>
                </div>
              </div>
            ) : step === 0 ? (
              <div data-testid="free-step1">
                <p className="mt-3 text-[11px] font-mono-edge tracking-[0.2em] uppercase text-sky-600">Step 1 of 2 · Your profile &amp; goal</p>
                <h2 className="mt-1 text-[22px] font-bold text-slate-900 leading-tight">
                  Get a done-for-you LinkedIn makeover — free.
                </h2>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">
                  Paste your LinkedIn URL, tell us what you&apos;re after, and we&apos;ll email
                  a fully audited, rewritten profile to your inbox. No cost, no catch.
                </p>

                <label htmlFor="free-linkedin-url" className="mt-4 block text-[12px] font-semibold text-slate-800">
                  Your LinkedIn profile URL
                </label>
                <input
                  id="free-linkedin-url"
                  type="url"
                  value={linkedinUrl}
                  onChange={(e) => setLinkedinUrl(e.target.value)}
                  onFocus={() => setErrors((x) => ({ ...x, linkedin_url: null }))}
                  placeholder="https://linkedin.com/in/your-handle"
                  className={`mt-1.5 w-full rounded-xl border-2 px-3.5 py-2.5 text-[14px] text-slate-900 outline-none transition ${
                    errors.linkedin_url ? 'border-rose-400 focus:border-rose-500' : 'border-slate-200 focus:border-sky-500'
                  }`}
                  data-testid="free-linkedin-input"
                  autoComplete="url"
                />
                {errors.linkedin_url && (
                  <p className="mt-1 text-[11.5px] text-rose-600" data-testid="free-linkedin-error">{errors.linkedin_url}</p>
                )}

                <p className="mt-4 text-[12px] font-semibold text-slate-800">Your primary goal</p>
                <div className="mt-1.5 space-y-2.5" role="radiogroup">
                  {GOALS.map((g) => {
                    const active = goal === g.key;
                    const Icon = g.icon;
                    return (
                      <button
                        key={g.key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setGoal(g.key)}
                        data-testid={`free-goal-${g.key}`}
                        className={`w-full text-left rounded-2xl border-2 px-4 py-3 transition-all flex items-start gap-3 ${
                          active
                            ? 'border-sky-500 bg-sky-50/70 shadow-[0_10px_30px_-15px_rgba(56,189,248,0.5)]'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <span className={`mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl ${active ? 'bg-sky-500 text-white' : 'bg-sky-100 text-sky-700'}`}>
                          <Icon size={16} />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[14px] font-semibold text-slate-900 leading-tight">{g.label}</span>
                          <span className="block text-[12.5px] text-slate-600 mt-0.5 leading-snug">{g.body}</span>
                        </span>
                        {active && <CheckCircle2 size={16} className="text-sky-500 ml-auto shrink-0 mt-1" />}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={handleContinue}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-yellow-400 hover:bg-yellow-300 text-slate-900 text-[14.5px] font-bold h-11 transition-colors"
                  data-testid="free-cta-step1"
                >
                  Tell us where to send your new profile <ArrowRight size={16} />
                </button>
                <p className="mt-2 text-[11px] text-slate-500 text-center">
                  Fully audited, rewritten LinkedIn profile — emailed within 24 hours.
                </p>
              </div>
            ) : (
              <div data-testid="free-step2">
                <p className="mt-3 text-[11px] font-mono-edge tracking-[0.2em] uppercase text-sky-600">Step 2 of 2 · Where should we send it?</p>
                <h2 className="mt-1 text-[22px] font-bold text-slate-900 leading-tight">
                  A few last details — and your new profile is on its way.
                </h2>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">
                  We&apos;ll email your complete audit and rewritten LinkedIn profile within 24 hours.
                  Your details stay private — no marketing spam, promise.
                </p>

                <div className="mt-4 space-y-3">
                  <div>
                    <label htmlFor="free-name" className="block text-[12px] font-semibold text-slate-800">Full name</label>
                    <input
                      id="free-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onFocus={() => setErrors((x) => ({ ...x, name: null }))}
                      placeholder="Jane Doe"
                      autoComplete="name"
                      className={`mt-1.5 w-full rounded-xl border-2 px-3.5 py-2.5 text-[14px] text-slate-900 outline-none transition ${
                        errors.name ? 'border-rose-400 focus:border-rose-500' : 'border-slate-200 focus:border-sky-500'
                      }`}
                      data-testid="free-name-input"
                    />
                    {errors.name && <p className="mt-1 text-[11.5px] text-rose-600">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="free-email" className="block text-[12px] font-semibold text-slate-800">Email — where we send your new profile</label>
                    <input
                      id="free-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setErrors((x) => ({ ...x, email: null }))}
                      placeholder="jane@company.com"
                      autoComplete="email"
                      className={`mt-1.5 w-full rounded-xl border-2 px-3.5 py-2.5 text-[14px] text-slate-900 outline-none transition ${
                        errors.email ? 'border-rose-400 focus:border-rose-500' : 'border-slate-200 focus:border-sky-500'
                      }`}
                      data-testid="free-email-input"
                    />
                    {errors.email && <p className="mt-1 text-[11.5px] text-rose-600">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="free-phone" className="block text-[12px] font-semibold text-slate-800">Phone (for delivery confirmation)</label>
                    <input
                      id="free-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      onFocus={() => setErrors((x) => ({ ...x, phone: null }))}
                      placeholder="+91 98765 43210"
                      autoComplete="tel"
                      className={`mt-1.5 w-full rounded-xl border-2 px-3.5 py-2.5 text-[14px] text-slate-900 outline-none transition ${
                        errors.phone ? 'border-rose-400 focus:border-rose-500' : 'border-slate-200 focus:border-sky-500'
                      }`}
                      data-testid="free-phone-input"
                    />
                    {errors.phone && <p className="mt-1 text-[11.5px] text-rose-600">{errors.phone}</p>}
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-3.5">
                  <label
                    htmlFor="free-consent"
                    className={`flex items-start gap-2.5 cursor-pointer text-[12px] leading-[1.55] text-slate-700 ${errors.consent ? 'text-rose-700' : ''}`}
                    data-testid="free-consent-label"
                  >
                    <input
                      id="free-consent"
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => {
                        setConsent(e.target.checked);
                        if (e.target.checked) setErrors((x) => ({ ...x, consent: null }));
                      }}
                      className={`mt-0.5 h-4 w-4 rounded border-2 accent-sky-600 cursor-pointer ${
                        errors.consent ? 'border-rose-400' : 'border-slate-300'
                      }`}
                      data-testid="free-consent-checkbox"
                    />
                    <span>
                      I confirm this is <b>my own LinkedIn profile</b>, or I&apos;m authorised to have it audited and rewritten.
                      I agree to Blayzrr&apos;s{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-sky-700 underline font-semibold">Terms &amp; Conditions</a>{' '}
                      and consent to being contacted by Blayzrr about special offers.
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="mt-1.5 pl-6 text-[11.5px] text-rose-600" data-testid="free-consent-error">{errors.consent}</p>
                  )}
                </div>

                <div className="mt-5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setStep(0)}
                    className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-200 bg-white text-slate-700 text-[13px] font-semibold h-11 px-4"
                    data-testid="free-back-step2"
                  >
                    <ArrowLeft size={15} /> Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-slate-900 text-[14.5px] font-bold h-11 transition-colors"
                    data-testid="free-cta-step2"
                  >
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                    {submitting ? 'Sending…' : 'Send me my new profile'}
                    {!submitting && <ArrowRight size={16} />}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default FreeGiveawayHero;
